if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.primeCartExperience = this.primeCartExperience.bind(this);
        this.hideErrors = this.dataset.hideErrors === 'true';

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
        ['pointerenter', 'touchstart', 'focus'].forEach((eventName) => {
          this.submitButton.addEventListener(eventName, this.primeCartExperience, {
            once: true,
            passive: true,
          });
        });
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(this.primeCartExperience, { timeout: 1200 });
        } else {
          window.setTimeout(this.primeCartExperience, 900);
        }
      }

      async onSubmitHandler(event) {
        event.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.primeCartExperience();
        this.handleErrorMessage();
        this.setLoading(true);
        this.cart?.showPending?.();

        const formData = new FormData(this.form);
        const variantId = Number(formData.get('id'));
        const sections = this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [];
        if (this.cart) this.cart.setActiveElement(document.activeElement);

        let request = this.defaultRequest(formData, sections);
        try {
          const giftRequest = await window.TuMandoGiftAutoAdd?.buildAddRequest?.(
            formData,
            sections,
            window.location.pathname,
          );
          if (giftRequest) request = giftRequest;
        } catch (_) {
          // A cart-state timeout must never block the main product add.
        }

        try {
          const networkResponse = await this.fetchWithOneRateLimitRetry(request.url, request.options);
          const response = await networkResponse.json();
          if (!networkResponse.ok || response.status) {
            this.handleCartError(response, variantId);
            return;
          }

          if (Array.isArray(response.items) && !response.key) {
            const mainItem = response.items.find((item) => Number(item.variant_id || item.id) === variantId);
            response.key = mainItem?.key || response.items[0]?.key;
          }

          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          this.publishCartUpdate(response, variantId);
          this.renderCart(response);
          window.TuMandoGiftAutoAdd?.afterAdd?.({
            variantId,
            giftIncluded: request.giftIncluded === true,
            cartData: response,
          });
        } catch (error) {
          console.error(error);
          this.cart?.cancelPending?.();
          this.handleErrorMessage('We could not add this item. Please try again.');
        } finally {
          this.setLoading(false);
          window.CartPerformance?.measureFromEvent?.('add:user-action', event);
        }
      }

      defaultRequest(formData, sections) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        if (sections.length) {
          formData.append('sections', sections);
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;
        return { url: routes.cart_add_url, options: config, giftIncluded: false };
      }

      async fetchWithTimeout(url, options = {}, timeoutMs = 7000) {
        const controller = new AbortController();
        const sourceSignal = options.signal;
        const relayAbort = () => controller.abort(sourceSignal?.reason);
        if (sourceSignal?.aborted) relayAbort();
        else sourceSignal?.addEventListener('abort', relayAbort, { once: true });
        const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
        try {
          return await fetch(url, { ...options, signal: controller.signal });
        } finally {
          window.clearTimeout(timeout);
          sourceSignal?.removeEventListener('abort', relayAbort);
        }
      }

      async fetchWithOneRateLimitRetry(url, options) {
        let response = await this.fetchWithTimeout(url, options, 7000);
        if (response.status !== 429) return response;
        await new Promise((resolve) => window.setTimeout(resolve, 250 + Math.floor(Math.random() * 120)));
        response = await this.fetchWithTimeout(url, options, 4000);
        return response;
      }

      handleCartError(response, variantId) {
        this.cart?.cancelPending?.();
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'product-form',
          productVariantId: variantId,
          errors: response.errors || response.description,
          message: response.message,
        });
        this.handleErrorMessage(response.description || response.message || 'Please try again.');
        const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
        if (!soldOutMessage) return;
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButtonText.classList.add('hidden');
        soldOutMessage.classList.remove('hidden');
        this.error = true;
      }

      publishCartUpdate(response, variantId) {
        this.error = false;
        const result = publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: variantId,
          cartData: response,
        });
        if (result?.catch) result.catch(() => {});
        document.dispatchEvent(new CustomEvent('tm:cart-add-confirmed', { detail: { variantId } }));
      }

      renderCart(response) {
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener('modalClosed', () => this.cart.renderContents(response), { once: true });
          quickAddModal.hide(true);
          return;
        }
        this.cart.renderContents(response);
      }

      setLoading(loading) {
        if (loading) {
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else if (!this.error) {
          this.submitButton.removeAttribute('aria-disabled');
        }
        this.submitButton.classList.toggle('loading', loading);
        this.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
        if (!loading && this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;
        this.errorMessageWrapper ||= this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage ||= this.errorMessageWrapper.querySelector('.product-form__error-message');
        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage && this.errorMessage) this.errorMessage.textContent = errorMessage;
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      primeCartExperience() {
        if (this._cartExperiencePrimed) return;
        this._cartExperiencePrimed = true;
        window.TuMandoGiftAutoAdd?.prime?.();
        window.__tmWarmCheckout?.();
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    },
  );
}
